import { Badge, Box, Button, Flex, HStack, SimpleGrid, Text, useColorModeValue, useToast } from '@chakra-ui/react';
import FileSaver from 'file-saver';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { FaFilePdf } from 'react-icons/fa';
import { MdOutlineLink } from 'react-icons/md';
import { downloadChargeBilletPdf } from 'services/api.service';
import { Billet, Charge, CreditCardInfo, ItemChargeStatus, Pix, PixRefund } from 'types/Charge';
import { maskCurrency } from 'utils/number';
import CopyableText from 'components/CopyableText';
import { DefinitionRow, InfoPanel } from 'components/DetailSection';
import { blobErrorMessage } from 'views/access/carnets/carnet';
import { BilletPixStatusRender, ChargeStatus, CreditCardStatusRender } from '.';
import RefundReceiptActions from './RefundReceiptActions';
import {
  buildCardRefundReceipt,
  buildPixRefundReceipt,
  canIssueCardRefundReceipt,
  canIssuePixRefundReceipt,
} from './refundReceiptData';
import SeeQrCode from './SeeQrCode';

const formatDate = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat('dd/MM/yyyy HH:mm:ss') : '---';

const REFUND_LABEL: Record<string, { label: string; colorScheme: string }> = {
  [ItemChargeStatus.FINISHED]: { label: 'Devolvido', colorScheme: 'green' },
  [ItemChargeStatus.PENDING]: { label: 'Em processamento', colorScheme: 'yellow' },
};

const RefundRow: FC<{ refund: PixRefund; pix: Pix; charge: Charge }> = ({ refund, pix, charge }) => {
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const badge = REFUND_LABEL[refund.status] ?? { label: 'Não realizado', colorScheme: 'red' };

  return (
    <Box p={2} mb={2} borderWidth="1px" borderRadius="md" borderColor={border}>
      <Flex justify="space-between" align="baseline" gap={2} flexWrap="wrap">
        <Text fontSize="sm" fontWeight="medium">
          {maskCurrency(refund.amount)}
        </Text>
        <Badge colorScheme={badge.colorScheme} fontSize="xs">
          {badge.label}
        </Badge>
      </Flex>
      <Text fontSize="xs" color={muted}>
        {formatDate(refund.createdAt)}
      </Text>
      {refund.endToEndId && (
        <Text fontSize="xs" color={muted} isTruncated>
          {refund.endToEndId}
        </Text>
      )}
      {canIssuePixRefundReceipt(refund) && (
        <Box mt={2}>
          <RefundReceiptActions size="xs" data={buildPixRefundReceipt(charge, pix, refund)} />
        </Box>
      )}
    </Box>
  );
};

const PixPanel: FC<{ pix: Pix; charge: Charge }> = ({ pix, charge }) => {
  const toast = useToast();

  return (
    <InfoPanel title="Pix" action={BilletPixStatusRender(pix.status)}>
      <DefinitionRow label="Valor">{maskCurrency(pix.amount)}</DefinitionRow>
      <DefinitionRow label="Criado em">{formatDate(pix.createdAt)}</DefinitionRow>
      <DefinitionRow label="TxId">
        <CopyableText value={pix.referenceIntegrationId} label="TxId" />
      </DefinitionRow>
      <DefinitionRow label="EndToEndId">
        <CopyableText value={pix.endToEndId} label="EndToEndId" />
      </DefinitionRow>
      <DefinitionRow label="ID">
        <CopyableText value={pix.id} label="ID do Pix" />
      </DefinitionRow>

      {!!pix.refunds?.length && (
        <Box mt={3}>
          <Text fontWeight="semibold" fontSize="xs" mb={2}>
            Devoluções
          </Text>
          {pix.refunds.map((refund) => (
            <RefundRow key={refund.id} refund={refund} pix={pix} charge={charge} />
          ))}
        </Box>
      )}

      {pix.status === ItemChargeStatus.PENDING && pix.qrCode && (
        <HStack mt={3} spacing={2} translate="no">
          <Button
            size="sm"
            colorScheme="brand"
            variant="outline"
            leftIcon={<MdOutlineLink />}
            onClick={() => {
              navigator.clipboard.writeText(pix.qrCode);
              toast({
                title: 'Sucesso',
                description: 'Código copiado com sucesso',
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            }}
          >
            Copiar código
          </Button>
          <SeeQrCode data={{ qrCode: pix.qrCode, amount: +charge.amount }} />
        </HStack>
      )}
    </InfoPanel>
  );
};

const CreditCardPanel: FC<{ creditCard: CreditCardInfo; charge: Charge }> = ({ creditCard, charge }) => (
  <InfoPanel title="Cartão de crédito" action={CreditCardStatusRender(creditCard.status)}>
    <DefinitionRow label="Valor">{maskCurrency(creditCard.amount)}</DefinitionRow>
    {+creditCard.amountRefunded > 0 && (
      <DefinitionRow label="Devolvido">{maskCurrency(creditCard.amountRefunded)}</DefinitionRow>
    )}
    <DefinitionRow label="Criado em">{formatDate(creditCard.createdAt)}</DefinitionRow>
    {creditCard.refundedAt && (
      <DefinitionRow label="Estornado em">{formatDate(creditCard.refundedAt)}</DefinitionRow>
    )}
    <DefinitionRow label="Cartão">{creditCard.creditCard || '---'}</DefinitionRow>
    <DefinitionRow label="Bandeira">{creditCard.brand || '---'}</DefinitionRow>
    <DefinitionRow label="Parcelas">{creditCard.installments ?? '---'}</DefinitionRow>
    <DefinitionRow label="Autorização">{creditCard.authorizationCode || '---'}</DefinitionRow>
    <DefinitionRow label="NSU">{creditCard.nsu || '---'}</DefinitionRow>
    <DefinitionRow label="Provedor">{creditCard.type || '---'}</DefinitionRow>
    <DefinitionRow label="Id adquirente">
      <CopyableText value={creditCard.paymentId} label="Id adquirente" />
    </DefinitionRow>
    {creditCard.returnMessage && <DefinitionRow label="Mensagem da API">{creditCard.returnMessage}</DefinitionRow>}

    {canIssueCardRefundReceipt(creditCard) && (
      <Box mt={3}>
        <RefundReceiptActions data={buildCardRefundReceipt(charge, creditCard)} />
      </Box>
    )}
  </InfoPanel>
);

const BilletPanel: FC<{ billet: Billet; chargeId: string }> = ({ billet, chargeId }) => {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  // Via baixada não é mais pagável: oferecer o PDF dela seria entregar um boleto
  // que o banco recusa. A paga continua reimprimível (o servidor também a aceita).
  const printable = billet.status !== ItemChargeStatus.CANCELED;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await downloadChargeBilletPdf(chargeId);
      const fileName = `boleto-${chargeId}.pdf`;
      FileSaver.saveAs(data, fileName);
    } catch (err) {
      // O 422 explica por que a ficha não pôde ser desenhada e manda usar o PDF do
      // banco, que está no botão ao lado: a mensagem sobe inteira.
      toast({
        title: 'Erro ao baixar o boleto',
        description: await blobErrorMessage(err, 'Erro ao baixar o boleto.'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <InfoPanel title="Boleto" action={BilletPixStatusRender(billet.status)}>
      <DefinitionRow label="Criado em">{formatDate(billet.createdAt)}</DefinitionRow>
      <DefinitionRow label="Seu número">{billet.billetId || '---'}</DefinitionRow>
      <DefinitionRow label="Código">{billet.billetCode || '---'}</DefinitionRow>
      <DefinitionRow label="Código de barras">
        <CopyableText value={billet.billetDigit} label="código de barras" />
      </DefinitionRow>

      <HStack mt={3} spacing={2} flexWrap="wrap">
        <Button
          size="sm"
          colorScheme="brand"
          variant="outline"
          translate="no"
          leftIcon={<MdOutlineLink />}
          onClick={() => {
            navigator.clipboard.writeText(billet.billetDigit);
            toast({
              title: 'Sucesso',
              description: 'Boleto copiado com sucesso',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }}
        >
          Copiar boleto
        </Button>
        {printable && (
          <Button
            size="sm"
            colorScheme="brand"
            leftIcon={<FaFilePdf />}
            isLoading={downloading}
            onClick={handleDownload}
          >
            Baixar boleto
          </Button>
        )}
        {/* Saída de emergência, como no app: o PDF oficial do banco. */}
        {printable && billet.billetUrl && (
          <Button
            as="a"
            href={billet.billetUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            variant="ghost"
            colorScheme="brand"
          >
            PDF do banco
          </Button>
        )}
      </HStack>
    </InfoPanel>
  );
};

/**
 * Os meios de pagamento da cobrança.
 *
 * Antes eram três painéis fixos, e os dois que não existiam ocupavam metade da tela dizendo
 * "não há informações". Agora só aparece o que existe; o que falta vira uma linha de ação no
 * rodapé, e só quando gerar aquele instrumento é de fato possível.
 */
const ChargePayments: FC<{
  data: Charge;
  onGenerate: (type: 'pix' | 'billet') => void;
}> = ({ data, onGenerate }) => {
  const muted = useColorModeValue('gray.500', 'whiteAlpha.600');

  const pixes = data?.Pix ?? [];
  const cards = data?.CreditCardPayment ?? [];
  const billets = data?.Billet ?? [];
  // Condições preservadas do código anterior: o Pix podia ser gerado a qualquer momento, o
  // boleto só com a cobrança pendente. A assimetria é antiga e não é assunto desta mudança.
  const canGeneratePix = !pixes.length;
  const canGenerateBillet = !billets.length && data?.status === ChargeStatus.PENDING;
  const hasAny = pixes.length + cards.length + billets.length > 0;

  return (
    <>
      {hasAny && (
        <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} spacing={4}>
          {pixes.map((pix) => (
            <PixPanel key={pix.id} pix={pix} charge={data} />
          ))}
          {cards.map((creditCard) => (
            <CreditCardPanel key={creditCard.id} creditCard={creditCard} charge={data} />
          ))}
          {billets.map((billet) => (
            <BilletPanel key={billet.id} billet={billet} chargeId={data.id} />
          ))}
        </SimpleGrid>
      )}

      {!hasAny && !canGeneratePix && !canGenerateBillet && (
        <Text fontSize="sm" color={muted}>
          Nenhum meio de pagamento foi gerado para esta cobrança.
        </Text>
      )}

      {(canGeneratePix || canGenerateBillet) && (
        <Flex align="center" gap={3} mt={hasAny ? 4 : 0} flexWrap="wrap">
          <Text fontSize="sm" color={muted}>
            {hasAny ? 'Gerar outro meio de pagamento:' : 'Nenhum meio de pagamento gerado ainda.'}
          </Text>
          {canGeneratePix && (
            <Button size="sm" colorScheme="brand" variant="outline" onClick={() => onGenerate('pix')}>
              Gerar Pix
            </Button>
          )}
          {canGenerateBillet && (
            <Button size="sm" colorScheme="brand" variant="outline" onClick={() => onGenerate('billet')}>
              Gerar boleto
            </Button>
          )}
        </Flex>
      )}
    </>
  );
};

export default ChargePayments;
